import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { collection, getAggregateFromServer, query, sum, where } from '@angular/fire/firestore';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { MembershipV2Service } from 'src/app/services/membership-v2.service';

export const GARANTI_STATUSES: string[] = ['Fakturert', 'Utsatt', 'Avsluttet'];

// Statuses a row can be left in besides the four fixed ones above.
export const GARANTI_STATUS_NONE = '';

// Norwegian food VAT — fixed regardless of a customer's own invoice VAT-rate
// (some employer invoices are VAT-exempt, e.g. schools, but the underlying
// canteen sales to individuals are always taxed at the food rate). Mirrors
// the manual spreadsheet, which divides every row by 1.15 unconditionally.
export const TURNOVER_VAT_RATE = 0.15;

// Kickback formula constant from the manual spreadsheet:
// "mat + drikke ink mva x 0,965 x kickback% = kickback salg".
export const KICKBACK_FACTOR = 0.965;

export interface GarantiSystem {
  systemId: string;          // the system_info doc id (canonical)
  alias: string | null;      // the system_info doc's own `systemId` field — other data (juice, Vendon) may use either
  name: string;
  number: any;
  companyId: string;
  garantiomsetning: number;  // ex-VAT guaranteed monthly turnover — lives on the system, edited via the System card, NOT on this page.
  kundekortId: string | null; // who pays the shortfall — see resolveGarantiPayer(); null when nobody can be picked safely.
  payerSource: GarantiPayerSource;
  linkedKundekortIds: string[]; // every live system-kunder link, for the picker and the "ambiguous" message.
  periodMonths: number;      // invoice the shortfall every N months (1 = monthly) — system_info.garantiPeriodMonths
  periodStart: string | null; // 'YYYY-MM' first month of the first period — system_info.garantiPeriodStart
}

// Which billing period a month falls in, for a system invoiced every N months.
// null = billed monthly (N <= 1, no start month, or before the start month).
// Periods run back to back from the start: start 2026-06, N 2 → Jun–Jul,
// Aug–Sep, … and the whole period is invoiced in its LAST month.
export function garantiPeriodFor(month: string, periodMonths: number, periodStart: string | null): { months: string[], isEnd: boolean } | null {
  const n = Math.floor(Number(periodMonths) || 1);
  if (n <= 1 || !periodStart) { return null; }
  const diff = moment(month, 'YYYY-MM').diff(moment(periodStart, 'YYYY-MM'), 'months');
  if (diff < 0) { return null; }
  const pos = diff % n;
  const first = moment(month, 'YYYY-MM').subtract(pos, 'months');
  const months = Array.from({ length: n }, (_x, i) => first.clone().add(i, 'months').format('YYYY-MM'));
  return { months, isEnd: pos === n - 1 };
}

export function garantiPeriodLabel(months: string[]): string {
  const a = moment(months[0], 'YYYY-MM'), b = moment(months[months.length - 1], 'YYYY-MM');
  return a.year() === b.year() ? `${a.format('MMMM')} – ${b.format('MMMM YYYY')}` : `${a.format('MMMM YYYY')} – ${b.format('MMMM YYYY')}`;
}

// Where a system's garanti payer came from:
//   'set'       — chosen explicitly (system_info.garantiKundekortId, Garantiomsetning tab).
//   'linked'    — no explicit payer, but the system is linked to exactly one kundekort.
//   'ambiguous' — no explicit payer and linked to several kundekort: never guess.
//   'none'      — no explicit payer and no link.
export type GarantiPayerSource = 'set' | 'linked' | 'ambiguous' | 'none';

export function resolveGarantiPayer(explicit: string | null, linked: string[], liveCustomer: Set<string>): { kundekortId: string | null, payerSource: GarantiPayerSource } {
  if (explicit && liveCustomer.has(explicit)) { return { kundekortId: explicit, payerSource: 'set' }; }
  if (linked.length === 1) { return { kundekortId: linked[0], payerSource: 'linked' }; }
  return { kundekortId: null, payerSource: linked.length ? 'ambiguous' : 'none' };
}

export interface GarantiCalcInput {
  garantiEksMva: number;
  kickbackRate: number | null;
  prosentIDrift: number;
  vendorAchievedIncVat: number;
  friggAppAchievedIncVat: number;
  // Coffee (Vendon) + juice (Juice tab) sold at the system — the client counts
  // both toward the guarantee (2026-09-30). See computeCoffeeJuiceIncVat().
  coffeeJuiceAchievedIncVat?: number;
}

// Pure calc functions shared between the Garantiomsetning table (display) and
// the Billing page's invoice builder (billable total) — kept in one place so
// the two never drift apart.
export function garantiAfterProsent(r: GarantiCalcInput) { return (Number(r.garantiEksMva) || 0) * (Number(r.prosentIDrift) || 0); }
export function sumAchievedIncVat(r: GarantiCalcInput) {
  return (Number(r.vendorAchievedIncVat) || 0) + (Number(r.friggAppAchievedIncVat) || 0) + (Number(r.coffeeJuiceAchievedIncVat) || 0);
}
export function sumAchievedExVat(r: GarantiCalcInput) { return sumAchievedIncVat(r) / (1 + TURNOVER_VAT_RATE); }
export function shortfallExVat(r: GarantiCalcInput) { return garantiAfterProsent(r) - sumAchievedExVat(r); }
export function kickbackAmount(r: GarantiCalcInput) { return r.kickbackRate ? sumAchievedIncVat(r) * KICKBACK_FACTOR * r.kickbackRate : 0; }
// The garanti line and the kickback are two separate invoice lines (2026-11-02):
// garanti = the shortfall, never below 0; kickback = a minus line (PowerOffice
// product 26) given IN FULL, even when sales beat the guarantee — so the two
// together, and the whole invoice, can be negative. They used to be netted into
// one garanti line floored at 0, which silently dropped Maxbo's kickback.
export function billableGaranti(r: GarantiCalcInput) { return Math.max(0, shortfallExVat(r)); }
export function netToInvoice(r: GarantiCalcInput) { return billableGaranti(r) - kickbackAmount(r); }

// Coffee + juice sold at one system in a month, inc. VAT.
export interface CoffeeJuiceTurnover {
  coffeeIncVat: number;
  juiceIncVat: number;
  incVat: number;
  detail: string;   // human-readable breakdown for the table's tooltip
}

export interface GarantiBillable {
  billable: number;
  kickback: number;  // positive amount ex VAT, invoiced as a minus line
  // period: set when several months are invoiced together (e.g. "juni – juli 2026").
  systems: { systemId: string, name: string, billable: number, kickback: number, period?: string }[];
}

@Injectable({ providedIn: 'root' })
export class GarantiomsetningService {

  constructor(private afs: AngularFirestore, private membership: MembershipV2Service) { }

  // ---- systems with a guaranteed turnover, joined to their payer ----------
  // Garantiomsetning belongs to the SYSTEM (set on the system_info doc /
  // System card — same field the sales-report's income-vs-garanti graph
  // already reads), not the customer. WHO PAYS the shortfall is a separate
  // choice: system_info.garantiKundekortId, set on the Garantiomsetning tab.
  // Without one, a system linked to exactly one kundekort (system-kunder)
  // bills that customer. A system linked to several used to bill whichever
  // link was read last (IKEA Vestby, Haavard Martinsens vei 30, Propcap in
  // Oct 2026) — now it resolves to nobody and invoicing asks for a payer.
  // Systems without a payer are still returned so they surface instead of
  // silently disappearing.
  async getGarantiSystems(): Promise<GarantiSystem[]> {
    const [sysSnap, linkSnap, kunderSnap] = await Promise.all([
      this.afs.collection('system_info').ref.get(),
      this.afs.collection('system-kunder').ref.get(),
      this.afs.collection('kunder').ref.get()
    ]);
    // A link whose kundekort was deleted is not an owner: deleting a customer
    // leaves its system-kunder links behind.
    const liveCustomer = new Set(kunderSnap.docs.map(d => d.id));
    const linksBySystem: { [systemId: string]: string[] } = {};
    linkSnap.docs.forEach(d => {
      const l = d.data() as any;
      if (l.systemId && l.kundekortId && liveCustomer.has(l.kundekortId)) {
        (linksBySystem[l.systemId] = linksBySystem[l.systemId] || []).push(l.kundekortId);
      }
    });
    const out: GarantiSystem[] = [];
    sysSnap.docs.forEach(d => {
      const s = d.data() as any;
      const g = Number(s.garantiomsetning);
      if (!g || g <= 0) { return; }
      // Links are keyed by whichever id was picked when connecting the system
      // (doc id or the human `systemId` field) — check both, matching the
      // dual lookup used elsewhere (e.g. kunde-kort-info-popup.loadSystems).
      const linked = [...(linksBySystem[d.id] || []), ...((s.systemId && linksBySystem[s.systemId]) || [])]
        .filter((x, i, a) => a.indexOf(x) === i);
      const payer = resolveGarantiPayer(s.garantiKundekortId || null, linked, liveCustomer);
      out.push({
        systemId: d.id, alias: s.systemId ? String(s.systemId) : null, name: s.name || d.id, number: s.number,
        companyId: s.companyId || '', garantiomsetning: g, linkedKundekortIds: linked, ...payer,
        periodMonths: Math.max(1, Math.floor(Number(s.garantiPeriodMonths) || 1)),
        periodStart: s.garantiPeriodStart || null
      });
    });
    return out;
  }

  // Sets (or clears, with null) who pays a system's garanti shortfall. Lives on
  // system_info only: it is billing metadata, read from nowhere else.
  setGarantiPayer(systemId: string, kundekortId: string | null, updatedBy?: string): Promise<void> {
    return this.afs.collection('system_info').doc(systemId).set({
      garantiKundekortId: kundekortId || null,
      garantiKundekortUpdatedAt: moment().valueOf(),
      garantiKundekortUpdatedBy: updatedBy || null
    }, { merge: true });
  }

  // Invoice a system's shortfall every N months from `start` ('YYYY-MM');
  // months <= 1 goes back to monthly.
  setGarantiPeriod(systemId: string, months: number, start: string | null, updatedBy?: string): Promise<void> {
    const n = Math.max(1, Math.floor(Number(months) || 1));
    return this.afs.collection('system_info').doc(systemId).set({
      garantiPeriodMonths: n,
      garantiPeriodStart: n > 1 ? (start || null) : null,
      garantiPeriodUpdatedAt: moment().valueOf(),
      garantiPeriodUpdatedBy: updatedBy || null
    }, { merge: true });
  }

  // ---- achieved Frigg-app turnover, inc. VAT -----------------------------
  // Sums the same field (`price`) the same way the existing sales-report
  // income-vs-garantiomsetning comparison does (SalesReportService.makeQuery
  // + getSalesRapportByGraphForAllSelectedSystems): per-system aggregate sum
  // over `transactions`, filtered to `status == true` (excludes refunded/void).
  async computeAppTurnoverIncVat(systemId: string, fromUnix: number, toUnix: number): Promise<number> {
    if (!systemId) { return 0; }
    const txCol = collection(this.afs.firestore, 'transactions');
    const q = query(txCol,
      where('systemId', '==', systemId),
      where('unix', '>=', fromUnix),
      where('unix', '<=', toUnix),
      where('status', '==', true));
    const agg = await getAggregateFromServer(q, { income: sum('price') });
    return Number(agg.data().income) || 0;
  }

  // ---- achieved coffee + juice turnover, inc. VAT -------------------------
  // Coffee comes from Vendon (every vendon-machines entry with a systemId: what
  // users paid at the machine plus what Frigg invoices the company for its
  // cups — garantiCoffeeTurnover.js). Juice from the Juice tab's entries for the
  // month: cups × price ex VAT, grossed up with food VAT like every other
  // achieved figure here. Keyed by the system_info doc id. THROWS when Vendon
  // cannot be read: a shortfall computed without the coffee would over-bill.
  async computeCoffeeJuiceIncVat(month: string, systems: GarantiSystem[]): Promise<{ [systemId: string]: CoffeeJuiceTurnover }> {
    const [coffee, juiceSnap]: [any, any] = await Promise.all([
      this.membership.garantiCoffeeTurnover(month).toPromise(),
      this.afs.firestore.collection('juice-entries').where('month', '==', month).get()
    ]);
    if (!coffee || coffee.success === false || (coffee.errors || []).length) {
      throw new Error(`Could not read coffee sales from Vendon for ${month}: ${(coffee && ((coffee.errors || []).join('; ') || coffee.message)) || 'no response'}`);
    }
    const juiceBySystem: { [id: string]: number } = {};
    juiceSnap.docs.forEach((d: any) => {
      const e = d.data();
      const amount = (Number(e.cups) || 0) * (Number(e.pricePerCupExVat) || 0) * (1 + TURNOVER_VAT_RATE);
      if (amount > 0) { juiceBySystem[String(e.systemId)] = (juiceBySystem[String(e.systemId)] || 0) + amount; }
    });
    const money = (n: number) => n.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const out: { [systemId: string]: CoffeeJuiceTurnover } = {};
    systems.forEach(sys => {
      const ids = [sys.systemId, sys.alias].filter((x, i, a) => x && a.indexOf(x) === i) as string[];
      const c = ids.map(id => coffee.bySystem && coffee.bySystem[id]).filter(Boolean);
      const coffeeIncVat = c.reduce((n: number, x: any) => n + (Number(x.incVat) || 0), 0);
      const juiceIncVat = ids.reduce((n, id) => n + (juiceBySystem[id] || 0), 0);
      const parts: string[] = [];
      c.forEach((x: any) => (x.machines || []).forEach((m: any) => parts.push(
        `${m.name}: ${m.cups} cups, ${money(m.salesIncVat)} paid at machine` + (m.billedIncVat ? ` + ${money(m.billedIncVat)} invoiced` : ''))));
      if (juiceIncVat) { parts.push(`Juice: ${money(juiceIncVat)}`); }
      out[sys.systemId] = {
        coffeeIncVat: +coffeeIncVat.toFixed(2),
        juiceIncVat: +juiceIncVat.toFixed(2),
        incVat: +(coffeeIncVat + juiceIncVat).toFixed(2),
        detail: parts.join('\n') || 'No coffee machine or juice registered for this system'
      };
    });
    return out;
  }

  // ---- monthly editable/computed row (one doc per system per month) ------
  private rowId(systemId: string, month: string) { return `${systemId}_${month}`; }

  getMonthRows$(month: string): Observable<any[]> {
    return this.afs.collection('garantiomsetning', ref => ref.where('month', '==', month))
      .snapshotChanges().pipe(map(docs => docs.map(d => ({ ...(d.payload.doc.data() as any), dbid: d.payload.doc.id }))));
  }

  saveRow(systemId: string, month: string, kundekortId: string | null, patch: any, updatedBy?: string): Promise<void> {
    const payload = { ...patch, systemId, kundekortId: kundekortId || null, month, updatedAt: moment().valueOf(), updatedBy: updatedBy || null };
    return this.afs.collection('garantiomsetning').doc(this.rowId(systemId, month)).set(payload, { merge: true });
  }

  // Billable garantiomsetning shortfall per kundekort for a month — used by
  // the Billing page to fold the shortfall into that customer's invoice.
  // Sums across every system linked to the same customer (a customer can
  // have more than one system, each with its own guarantee).
  async getBillableByCustomer(month: string): Promise<{ [kundekortId: string]: GarantiBillable }> {
    const [systems, savedSnap, kunderSnap] = await Promise.all([
      this.getGarantiSystems(),
      // afs.firestore, not afs.collection(path, q => ...).ref: `.ref` is the bare
      // collection and silently drops the where(), which read every month's rows.
      this.afs.firestore.collection('garantiomsetning').where('month', '==', month).get(),
      this.afs.collection('kunder').ref.get()
    ]);
    const bySystem: { [id: string]: any } = {};
    savedSnap.docs.forEach(d => { const v = d.data() as any; if (v.systemId) { bySystem[v.systemId] = v; } });

    const kickbackByCustomer: { [id: string]: number | null } = {};
    kunderSnap.docs.forEach(d => {
      const v = d.data() as any;
      kickbackByCustomer[d.id] = v.kickbackRate != null ? Number(v.kickbackRate) : null;
    });

    // Frigg-app turnover is recomputed for the WHOLE month here, never taken from
    // the saved row: a row saved mid-month is a stale snapshot. August 2026 had
    // Palfinger saved on 24 Aug at 21 436 against a real 29 885 — ~7 300 kr
    // over-billed had it been used. The saved value remains the fallback only if
    // the live sum fails.
    const start = moment.tz(`${month}-01`, 'Europe/Oslo').startOf('month');
    const fromUnix = start.unix();
    const toUnix = start.clone().endOf('month').unix();
    const liveApp: { [systemId: string]: number } = {};
    await Promise.all(systems.map(async s => {
      try { liveApp[s.systemId] = await this.computeAppTurnoverIncVat(s.systemId, fromUnix, toUnix); } catch (e) { /* fallback below */ }
    }));
    // Coffee + juice, live for the same reason. No fallback: this throws, and
    // invoicing stops, rather than billing a shortfall that ignores the coffee.
    const coffeeJuice = await this.computeCoffeeJuiceIncVat(month, systems);

    const out: { [kundekortId: string]: GarantiBillable } = {};
    const noPayer: string[] = [];
    const periodic: GarantiSystem[] = [];
    systems.forEach(sys => {
      const period = garantiPeriodFor(month, sys.periodMonths, sys.periodStart);
      if (period) { if (period.isEnd) { periodic.push(sys); } return; }
      const s = bySystem[sys.systemId] || {};
      const input: GarantiCalcInput = {
        garantiEksMva: sys.garantiomsetning,
        kickbackRate: sys.kundekortId ? (kickbackByCustomer[sys.kundekortId] ?? null) : null,
        prosentIDrift: s.prosentIDrift != null ? s.prosentIDrift : 1,
        vendorAchievedIncVat: s.vendorAchievedIncVat != null ? s.vendorAchievedIncVat : 0,
        friggAppAchievedIncVat: liveApp[sys.systemId] != null ? liveApp[sys.systemId]
          : (s.friggAppAchievedIncVat != null ? s.friggAppAchievedIncVat : 0),
        coffeeJuiceAchievedIncVat: coffeeJuice[sys.systemId] ? coffeeJuice[sys.systemId].incVat : 0,
      };
      const amt = billableGaranti(input);
      const kick = kickbackAmount(input);
      if (!(amt > 0) && !(kick > 0)) { return; }
      // A shortfall with nobody to bill used to be dropped without a word.
      // (No payer means no kickback either — the rate lives on the payer's card.)
      if (!sys.kundekortId) {
        noPayer.push(`${sys.name} (${amt.toFixed(2)} kr, ${sys.payerSource === 'ambiguous' ? 'linked to several customers' : 'not linked to any customer'})`);
        return;
      }
      const bucket = out[sys.kundekortId] || (out[sys.kundekortId] = { billable: 0, kickback: 0, systems: [] });
      bucket.billable += amt;
      bucket.kickback += kick;
      bucket.systems.push({ systemId: sys.systemId, name: sys.name, billable: +amt.toFixed(2), kickback: +kick.toFixed(2) });
    });
    await this.addPeriodBillable(month, periodic, kickbackByCustomer, coffeeJuice, out, noPayer);
    if (noPayer.length) {
      throw new Error(`No one is set to pay the garantiomsetning shortfall for: ${noPayer.join('; ')}. Choose "Paid by" for these systems on the Garantiomsetning tab.`);
    }
    Object.values(out).forEach(b => { b.billable = +b.billable.toFixed(2); b.kickback = +b.kickback.toFixed(2); });
    return out;
  }

  // Systems invoiced every N months whose period ends this month. The whole
  // period is billed at once, and every system with the same payer and period
  // is pooled into ONE guarantee — Tesla Drammen's 25 000 a month covers both of
  // its systems, so a busy floor offsets a quiet one. Each month still uses its
  // own saved row (% i drift, vendor sales); Frigg-app sales are summed live
  // over the whole period, coffee + juice per month.
  private async addPeriodBillable(month: string, systems: GarantiSystem[], kickbackByCustomer: { [id: string]: number | null },
                                  coffeeJuiceThisMonth: { [systemId: string]: CoffeeJuiceTurnover },
                                  out: { [kundekortId: string]: GarantiBillable }, noPayer: string[]): Promise<void> {
    if (!systems.length) { return; }
    const pools: { [key: string]: { payer: string | null, months: string[], systems: GarantiSystem[] } } = {};
    systems.forEach(sys => {
      const period = garantiPeriodFor(month, sys.periodMonths, sys.periodStart);
      const key = `${sys.kundekortId || sys.systemId}|${period.months.join(',')}`;
      (pools[key] = pools[key] || { payer: sys.kundekortId, months: period.months, systems: [] }).systems.push(sys);
    });

    const allMonths = Array.from(new Set(([] as string[]).concat(...Object.values(pools).map(p => p.months))));
    const rowsSnap = await Promise.all(allMonths.map(m => this.afs.firestore.collection('garantiomsetning').where('month', '==', m).get()));
    const row: { [systemMonth: string]: any } = {};
    rowsSnap.forEach(snap => snap.docs.forEach(d => { const v = d.data() as any; row[`${v.systemId}|${v.month}`] = v; }));
    const coffeeJuiceByMonth: { [m: string]: { [systemId: string]: CoffeeJuiceTurnover } } = { [month]: coffeeJuiceThisMonth };
    for (const m of allMonths) {
      if (!coffeeJuiceByMonth[m]) { coffeeJuiceByMonth[m] = await this.computeCoffeeJuiceIncVat(m, systems); }
    }

    for (const pool of Object.values(pools)) {
      const fromUnix = moment.tz(`${pool.months[0]}-01`, 'Europe/Oslo').startOf('month').unix();
      const toUnix = moment.tz(`${pool.months[pool.months.length - 1]}-01`, 'Europe/Oslo').endOf('month').unix();
      const input: GarantiCalcInput = {
        garantiEksMva: 0, prosentIDrift: 1, vendorAchievedIncVat: 0, friggAppAchievedIncVat: 0, coffeeJuiceAchievedIncVat: 0,
        kickbackRate: pool.payer ? (kickbackByCustomer[pool.payer] ?? null) : null
      };
      for (const sys of pool.systems) {
        pool.months.forEach(m => {
          const s = row[`${sys.systemId}|${m}`] || {};
          input.garantiEksMva += sys.garantiomsetning * (s.prosentIDrift != null ? s.prosentIDrift : 1);
          input.vendorAchievedIncVat += Number(s.vendorAchievedIncVat) || 0;
          const cj = coffeeJuiceByMonth[m][sys.systemId];
          input.coffeeJuiceAchievedIncVat += cj ? cj.incVat : 0;
        });
        input.friggAppAchievedIncVat += await this.computeAppTurnoverIncVat(sys.systemId, fromUnix, toUnix);
      }
      const amt = billableGaranti(input);
      const kick = kickbackAmount(input);
      if (!(amt > 0) && !(kick > 0)) { continue; }
      const name = pool.systems.map(s => s.name).join(' + ');
      if (!pool.payer) {
        noPayer.push(`${name} (${amt.toFixed(2)} kr, ${pool.systems[0].payerSource === 'ambiguous' ? 'linked to several customers' : 'not linked to any customer'})`);
        continue;
      }
      const bucket = out[pool.payer] || (out[pool.payer] = { billable: 0, kickback: 0, systems: [] });
      bucket.billable += amt;
      bucket.kickback += kick;
      bucket.systems.push({ systemId: pool.systems[0].systemId, name, billable: +amt.toFixed(2), kickback: +kick.toFixed(2), period: garantiPeriodLabel(pool.months) });
    }
  }
}
